import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Button,
  IconButton,
  CircularProgress,
  Box,
  Stack,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import Toaster from 'src/Toaster/Toaster';
import { HttpClient } from 'src/Helpers/http/http';
import moment from 'moment';
import PdfAndImageView from 'src/components/pdfAndImageView/PdfAndImageView';
import { imgUrl } from 'src/Helpers/utils/Constant';
import DeleteConfirmation from 'src/Utils/DeleteConfirmation';

interface AdditionalDocumentsProps {
  memberId: string;
}

const AdditionalDocuments = ({ memberId }: AdditionalDocumentsProps) => {
  const [additionalDocs, setAdditionalDocs] = useState<any[]>([]);
  const [docsLoading, setDocsLoading] = useState<boolean>(false);
  const [refreshDocs, setRefreshDocs] = useState<boolean>(false);
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<{ [key: string]: boolean }>({});

  // Deletion states
  const [docToDelete, setDocToDelete] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  const [imageViewOpen, SetImageViewOpen] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<string | null>('');

  const fetchAdditionalDocs = async () => {
    setDocsLoading(true);
    try {
      const res = await HttpClient.get<any>(
        `/api/atab/admin/member-company/member/additional-documents/${memberId}`
      );
      if (Array.isArray(res)) {
        setAdditionalDocs(res);
      } else if (res && res.success && Array.isArray(res.data)) {
        setAdditionalDocs(res.data);
      } else {
        setAdditionalDocs([]);
      }
    } catch (error) {
      console.error('Failed to fetch additional documents', error);
    } finally {
      setDocsLoading(false);
    }
  };

  useEffect(() => {
    if (memberId) {
      fetchAdditionalDocs();
    }
  }, [memberId, refreshDocs]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadLoading(true);
    const Toast = Toaster();
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        await HttpClient.post<any>(
          `/api/atab/admin/member-company/member/additional-documents/${memberId}`,
          formData
        );
      }
      Toast.fire({
        icon: 'success',
        title: 'Documents uploaded successfully',
      });
      setRefreshDocs((prev) => !prev);
    } catch (error: any) {
      Toast.fire({
        icon: 'error',
        title: error?.response?.data?.message || 'Failed to upload documents',
      });
    } finally {
      setUploadLoading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async () => {
    if (docToDelete === null) return;
    const Toast = Toaster();
    setDeleteLoading(true);
    try {
      const res = await HttpClient.delete<any>(
        `/api/atab/admin/member-company/member/additional-documents/${memberId}/${docToDelete}`
      );
      Toast.fire({
        icon: 'success',
        title: res.message || 'Document deleted successfully',
      });
      setRefreshDocs((prev) => !prev);
      setDocToDelete(null);
    } catch (error: any) {
      Toast.fire({
        icon: 'error',
        title: error?.response?.data?.message || 'Failed to delete document',
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleReplace = async (id: number, file: File) => {
    const Toast = Toaster();
    setActionLoading((prev) => ({ ...prev, [`replace_${id}`]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await HttpClient.patch<any>(
        `/api/atab/admin/member-company/member/additional-documents/${memberId}/${id}`,
        formData
      );
      Toast.fire({
        icon: 'success',
        title: res.message || 'Document replaced successfully',
      });
      setRefreshDocs((prev) => !prev);
    } catch (error: any) {
      Toast.fire({
        icon: 'error',
        title: error?.response?.data?.message || 'Failed to replace document',
      });
    } finally {
      setActionLoading((prev) => ({ ...prev, [`replace_${id}`]: false }));
    }
  };

  return (
    <>
      <Card sx={{ mt: 4, mb: 4, boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)' }}>
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={1}>
              <InsertDriveFileIcon color="primary" />
              <Typography variant="h4" fontWeight={700}>
                Additional Documents
              </Typography>
            </Stack>
          }
          action={
            <Box>
              <Button
                variant="contained"
                component="label"
                disabled={uploadLoading}
                startIcon={
                  uploadLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <CloudUploadIcon />
                  )
                }
                sx={{
                  textTransform: 'none',
                  borderRadius: 1,
                  fontWeight: 600,
                  px: 3,
                }}
              >
                {uploadLoading ? 'Uploading...' : 'Upload Files'}
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={handleUpload}
                  accept="image/*,application/pdf"
                />
              </Button>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          {docsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={30} />
            </Box>
          ) : additionalDocs.length === 0 ? (
            <Box py={4} textAlign="center">
              <Typography variant="body1" color="text.secondary">
                No additional documents uploaded yet.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #f0f0f0' }}>
              <Table aria-label="additional documents table">
                <TableBody>
                  {additionalDocs.map((doc: any, index: number) => {
                    const isPdf = doc.file?.toLowerCase().endsWith('.pdf');
                    const parts = doc.file?.split('/');
                    const filename = parts ? parts[parts.length - 1] : 'File';
                    const cleanFilename = filename.replace(/^\d+-/, '');

                    return (
                      <TableRow key={doc.id || index} hover>
                        <TableCell sx={{ py: 1.5 }}>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            {isPdf ? (
                              <PictureAsPdfIcon color="error" />
                            ) : (
                              <Box
                                component="img"
                                src={`${imgUrl}/${doc.file}`}
                                alt={cleanFilename}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 1,
                                  objectFit: 'cover',
                                  border: '1px solid #e0e0e0',
                                }}
                              />
                            )}
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                                {cleanFilename}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Uploaded at: {moment(doc.created_at).format('Do MMM YYYY, h:mm a')}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                            <Tooltip title="View Document">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => {
                                  SetImageViewOpen(true);
                                  setImageFile(doc.file);
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Replace File">
                              <IconButton
                                size="small"
                                color="warning"
                                component="label"
                                disabled={actionLoading[`replace_${doc.id}`]}
                              >
                                {actionLoading[`replace_${doc.id}`] ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  <EditIcon fontSize="small" />
                                )}
                                <input
                                  type="file"
                                  hidden
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      handleReplace(doc.id, file);
                                    }
                                    e.target.value = '';
                                  }}
                                  accept="image/*,application/pdf"
                                />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Delete Document">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDocToDelete(doc.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <PdfAndImageView
        setOpen={SetImageViewOpen}
        open={imageViewOpen}
        file={imageFile}
      />

      <DeleteConfirmation
        open={docToDelete !== null}
        handleClose={() => setDocToDelete(null)}
        text="Are you sure you want to delete this document?"
        handleDeleteFun={handleDelete}
        loading={deleteLoading}
      />
    </>
  );
};

export default AdditionalDocuments;
